import { FC, useRef } from 'react';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import ApproverNote from '../ApproverNote';
import ArtifactTable from '../ArtifactTable';
import DownloadReportButton from '../DownloadReportButton';
import { FinalizeTabProps } from '../ReportDialog.props';
import { formatWhen } from '../ReportDialog.utils';

const FinalizeTab: FC<FinalizeTabProps> = ({
  columns,
  rows,
  day,
  isFinalized,
  isStaff,
  isLocked,
  validationPassed,
  hasGeneratedReport,
  approverNote,
  onApproverNoteChange,
  approverNoteFiles,
  onApproverNoteFilesChange,
  canConfirmFinalize,
  isFinalizing,
  onFinalize,
  canDownloadReport,
  isDownloadingDocuments,
  onDownload,
  onDownloadAttachment,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box data-testid="daily-nav-report-finalize">
      <ArtifactTable columns={columns} rows={rows} />
      {isLocked && (
        <Typography variant="body2" mt={2} color="text.secondary" data-testid="daily-nav-report-locked">
          This Daily NAV day is closed because a later day has been generated. It can no longer be finalized.
        </Typography>
      )}
      {isFinalized ? (
        <Box mt={2}>
          <Typography variant="body2" data-testid="daily-nav-finalized-by">
            Finalized by {day?.finalized_by_name || 'Unknown'}
          </Typography>
          {day?.finalized_at && (
            <Typography variant="body2" mt={1} data-testid="daily-nav-finalized-at">
              Finalized at {formatWhen(day.finalized_at)}
            </Typography>
          )}
          <ApproverNote day={day} onDownloadAttachment={onDownloadAttachment} />
          {canDownloadReport && (
            <DownloadReportButton isDownloading={isDownloadingDocuments} onClick={onDownload} />
          )}
        </Box>
      ) : (
        <Box mt={2}>
          {!validationPassed && (
            <Typography variant="body2" color="error.main">
              Validation must pass before this day can be finalized.
            </Typography>
          )}
          {!hasGeneratedReport && validationPassed && (
            <Typography variant="body2" color="error.main">
              Generate and review the report preview before finalizing.
            </Typography>
          )}
          {isStaff && (
            <>
              <TextField
                id="daily-nav-finalize-approver-note"
                data-testid="staff-finalize-confirmation-input"
                fullWidth
                margin="dense"
                size="small"
                multiline
                minRows={3}
                label="Who approved these prices"
                value={approverNote}
                onChange={event => onApproverNoteChange(event.target.value)}
              />
              {approverNoteFiles.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                  {approverNoteFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${file.lastModified}`}
                      size="small"
                      label={file.name}
                      data-testid={`daily-nav-finalize-pending-file-${index}`}
                      onDelete={() =>
                        onApproverNoteFilesChange(approverNoteFiles.filter((_, fileIndex) => fileIndex !== index))
                      }
                    />
                  ))}
                </Box>
              )}
              {/* Files go to Documents > Daily NAV > <this report date> > final report. */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                data-testid="daily-nav-finalize-attach-file-input"
                onChange={event => {
                  const chosen = Array.from(event.target.files ?? []);
                  onApproverNoteFilesChange([...approverNoteFiles, ...chosen]);
                  // Reset so choosing the same file again still fires onChange.
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              />
              <Button
                id="daily-nav-finalize-attach-file-button"
                data-testid="daily-nav-finalize-attach-file-button"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<AttachFileOutlined sx={{ fontSize: 14 }} />}
                sx={{ mt: 0.5, textTransform: 'none' }}>
                Attach file
              </Button>
            </>
          )}
          <Button
            data-testid="confirm-finalize-daily-nav-button"
            variant="contained"
            sx={{ mt: 1, textTransform: 'none' }}
            disabled={!canConfirmFinalize || isFinalizing || isLocked}
            onClick={onFinalize}>
            Finalize
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FinalizeTab;
